/**
 * Shared client helpers for talking to the legacy SAP Commerce accelerator
 * storefront cart.
 *
 * d2c-web is served from the same origin as the accelerator, so these requests
 * carry its session cookie and operate on the very cart the storefront renders.
 * They must run in the browser: a server-side call would open its own
 * accelerator session and act on a cart the user cannot see.
 *
 * Every request here must carry "X-Requested-With: XMLHttpRequest". The
 * accelerator records the last non-AJAX URL it served as the session's return
 * page, and the country switch at /{country}/{lang}/country/{to}/{lang}
 * redirects to that path under the newly selected site. Without the header a
 * background cart request becomes that return page, so changing country lands
 * the browser on a JSON endpoint instead of a page.
 */

import {
  beginCartRequest,
  type CartFlowFields,
  describeBody,
  describeError,
  describeResponse,
  noteCartCountTrigger,
  readBodyText,
  recordCartCountSuccess,
  reportCartCountFailure,
  resourceTimingFields,
  takeCartCountTrigger,
} from "@/services/datadog/cart-flow-telemetry";

/** Fired after the accelerator cart changes so the header count can refresh. */
export const CART_UPDATED_EVENT = "scott:accelerator-cart-updated";
export const CART_UPDATED_STORAGE_KEY = "scott:accelerator-cart-updated-at";
/**
 * Fired only after the accelerator confirmed an add, so the header can show the
 * "added to cart" drawer. A rejected or unconfirmed add must not fire it.
 */
export const PRODUCT_ADDED_EVENT = "scott:accelerator-cart-product-added";

const DIGIT_STRING_PATTERN = /^\d+$/;

function normalizeMiniCartCount(value: unknown): number | null {
  let count: number;

  if (typeof value === "number") {
    count = value;
  } else if (typeof value === "string" && DIGIT_STRING_PATTERN.test(value)) {
    count = Number(value);
  } else {
    return null;
  }

  return Number.isSafeInteger(count) && count >= 0 ? count : null;
}

export function getStorefrontBasePath(
  country: string,
  language: string
): string {
  return `/${encodeURIComponent(country)}/${encodeURIComponent(language)}`;
}

export function notifyCartUpdated(): void {
  window.dispatchEvent(new Event(CART_UPDATED_EVENT));

  try {
    localStorage.setItem(
      CART_UPDATED_STORAGE_KEY,
      `${Date.now()}:${crypto.randomUUID()}`
    );
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts. The
    // same-document event above still keeps the current page synchronized.
  }
}

/** Same-document only: other tabs refresh their count but keep the drawer shut. */
export function notifyProductAdded(): void {
  window.dispatchEvent(new Event(PRODUCT_ADDED_EVENT));
}

/**
 * Calls the listener whenever the accelerator cart may have changed: on a cart
 * update in this or another tab, and when the page regains focus or is restored
 * from the back-forward cache. Returns the unsubscribe function.
 */
export function subscribeToCartUpdates(listener: () => void): () => void {
  // The trigger is noted right before the listener starts the refresh, so a
  // failed refresh can say what made it run.
  function triggeredBy(trigger: string) {
    return () => {
      noteCartCountTrigger(trigger);
      listener();
    };
  }

  const handleCartUpdated = triggeredBy("cartUpdated");
  const handleFocus = triggeredBy("focus");
  const handlePageShow = triggeredBy("pageshow");
  const refreshForOtherTab = triggeredBy("otherTab");

  function handleStorage(event: StorageEvent) {
    if (event.key === CART_UPDATED_STORAGE_KEY) {
      refreshForOtherTab();
    }
  }

  window.addEventListener(CART_UPDATED_EVENT, handleCartUpdated);
  window.addEventListener("focus", handleFocus);
  window.addEventListener("pageshow", handlePageShow);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdated);
    window.removeEventListener("focus", handleFocus);
    window.removeEventListener("pageshow", handlePageShow);
    window.removeEventListener("storage", handleStorage);
  };
}

export async function fetchAcceleratorCartCount(
  country: string,
  language: string
): Promise<number | null> {
  const basePath = getStorefrontBasePath(country, language);
  const request = beginCartRequest("cart_count");
  const startedAt = performance.now();
  const url = `${basePath}/cart/miniCart/SUBTOTAL`;
  // What made this refresh run: the first one on mount, later ones by event.
  const trigger = takeCartCountTrigger() ?? "mount";

  function reportFailure(reason: string, fields: CartFlowFields) {
    reportCartCountFailure({
      ...fields,
      ...resourceTimingFields(url, startedAt),
      ...request.sessionFields(),
      trigger,
      errorCategory: reason,
      locale: `${country}/${language}`,
      durationMs: Math.round(performance.now() - startedAt),
      inFlightCartRequests: request.overlapping(),
    });
  }

  try {
    const response = await fetch(url, {
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    request.received();

    if (!response.ok) {
      // Reading the body also lets the browser finish its timing entry before
      // it is looked up. Only its size and kind are logged, never its content.
      const body = await readBodyText(response);
      reportFailure("http_error", {
        ...describeResponse(response),
        ...describeBody(response, body),
      });
      return null;
    }

    let payload: unknown;

    try {
      payload = await response.json();
    } catch {
      reportFailure("bad_payload", {
        ...describeResponse(response),
        payloadProblem: "not_json",
      });
      return null;
    }

    if (
      typeof payload !== "object" ||
      payload === null ||
      !("miniCartCount" in payload)
    ) {
      reportFailure("bad_payload", {
        ...describeResponse(response),
        payloadProblem:
          typeof payload === "object" && payload !== null
            ? "no_count"
            : "not_an_object",
        payloadType: payload === null ? "null" : typeof payload,
      });
      return null;
    }

    const { miniCartCount } = payload;
    const count = normalizeMiniCartCount(miniCartCount);

    if (count === null) {
      reportFailure("bad_payload", {
        ...describeResponse(response),
        payloadProblem: "invalid_count",
        // The type, and the value only if it is a number.
        countType: typeof miniCartCount,
        countValue:
          typeof miniCartCount === "number" && Number.isFinite(miniCartCount)
            ? miniCartCount
            : undefined,
      });
    } else {
      recordCartCountSuccess();
    }

    return count;
  } catch (error) {
    reportFailure("request_failed", describeError(error));
    throw error;
  } finally {
    request.end();
  }
}
